import React, { useState } from 'react';
import { Button } from 'antd';
import './ZikrCarousel.css'; // For some basic grid styling, or create your own

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface ScheduleGridProps {
  value?: { [day: string]: number[] } | null;
  onChange?: (schedule: { [day: string]: number[] } | null) => void;
}

const defaultSchedule = () => {
  const obj: { [day: string]: number[] } = {};
  for (const d of DAYS) obj[d] = [];
  return obj;
};

function isFull(schedule: { [day: string]: number[] } | null) {
  if (!schedule) return true;
  return DAYS.every(day => schedule[day]?.length === 24);
}

const ScheduleGrid: React.FC<ScheduleGridProps> = ({ value, onChange }) => {
  const [dragging, setDragging] = useState<{ day: string; hour: number; add: boolean } | null>(null);
  const schedule = value || defaultSchedule();

  const handleCellClick = (day: string, hour: number) => {
    const hours = new Set(schedule[day] || []);
    if (hours.has(hour)) hours.delete(hour);
    else hours.add(hour);
    const newSchedule = { ...schedule, [day]: Array.from(hours).sort((a, b) => a - b) };
    if (onChange) onChange(newSchedule);
  };

  const handleCellMouseDown = (day: string, hour: number) => {
    const hours = new Set(schedule[day] || []);
    const add = !hours.has(hour);
    setDragging({ day, hour, add });
    handleCellClick(day, hour);
  };

  const handleCellMouseEnter = (day: string, hour: number) => {
    if (!dragging) return;
    const hours = new Set(schedule[day] || []);
    if (dragging.add) hours.add(hour);
    else hours.delete(hour);
    const newSchedule = { ...schedule, [day]: Array.from(hours).sort((a, b) => a - b) };
    if (onChange) onChange(newSchedule);
  };

  const handleMouseUp = () => setDragging(null);

  const handleFullTimeToggle = () => {
    if (isFull(schedule)) {
      // Set to empty (not visible at all)
      if (onChange) onChange(defaultSchedule());
    } else {
      // Set all to full
      const full: { [day: string]: number[] } = {};
      for (const d of DAYS) full[d] = Array.from({ length: 24 }, (_, i) => i);
      if (onChange) onChange(full);
    }
  };

  // Summary string
  const summary = isFull(schedule)
    ? 'Visible all week (24/7)'
    : DAYS.map((d, i) => {
        const hours = schedule[d] || [];
        if (hours.length === 0) return null;
        // Group consecutive hours
        let ranges: [number, number][] = [];
        let start = null;
        for (let h = 0; h < 24; h++) {
          if (hours.includes(h)) {
            if (start === null) start = h;
          } else {
            if (start !== null) {
              ranges.push([start, h - 1]);
              start = null;
            }
          }
        }
        if (start !== null) ranges.push([start, 23]);
        return `${DAY_LABELS[i]}: ` + ranges.map(([s, e]) => s === e ? `${s}:00` : `${s}:00-${e + 1}:00`).join(', ');
      }).filter(Boolean).join(' | ');

  return (
    <div style={{ userSelect: 'none' }} onMouseUp={handleMouseUp}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <b>Schedule:</b>
        <span style={{ fontSize: 13, color: '#666' }}>{summary}</span>
        <Button size="small" onClick={handleFullTimeToggle} style={{ marginLeft: 8 }}>
          {isFull(schedule) ? 'Clear All' : 'Set 24/7'}
        </Button>
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 6, background: '#fafbfc' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 60, textAlign: 'right', paddingRight: 8 }}></th>
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} style={{ fontSize: 10, fontWeight: 400, color: '#888', padding: 2, textAlign: 'left' }} title={`${h}:00–${h + 1}:00`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DAYS.map((day, i) => (
              <tr key={day}>
                <td style={{ textAlign: 'right', fontWeight: 500, color: '#444', paddingRight: 8 }}>{DAY_LABELS[i]}</td>
                {Array.from({ length: 24 }, (_, h) => {
                  const selected = (schedule[day] || []).includes(h);
                  return (
                    <td
                      key={h}
                      style={{
                        background: selected ? '#4f8cff' : 'transparent',
                        border: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        width: 18,
                        height: 18,
                        padding: 0,
                        transition: 'background 0.1s',
                      }}
                      onMouseDown={() => handleCellMouseDown(day, h)}
                      onMouseEnter={() => handleCellMouseEnter(day, h)}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
        <span>Each column = one hour (e.g. 14 = 14:00–15:00). Click or drag to select. Leave empty for no visibility, or set all for 24/7.</span>
      </div>
    </div>
  );
};

export default ScheduleGrid; 